import React from 'react';

import {
  noop,
  type ClientAdPlacement,
  type Nullable
} from '@schibsted-nmp/advertising-shared';

import type { useSearchPositionInstruction } from './useSearchPositionInstruction.js'; // eslint-disable-line @typescript-eslint/no-unused-vars
import {
  $placementsMap,
  getPlacementList,
  placementHasIntermingle
} from '../atoms/placements.js';

export type Intermingle = Pick<
  ClientAdPlacement,
  'placementId' | 'intermingle' | 'adServer'
>;

export namespace Intermingle {
  export type List = ReadonlyArray<Intermingle>;

  export namespace List {
    export function use(): List {
      const [intermingles, setIntermingles] = React.useState(get);
      const unsubscribeFromIntermingleSub = React.useRef<() => void>();

      React.useEffect(() => {
        if (intermingles.length !== 0) return noop;

        unsubscribeFromIntermingleSub.current = $placementsMap.subscribe(() => {
          setIntermingles(use.tryPreserveReferenceStateAction);
        });

        return () => {
          unsubscribeFromIntermingleSub.current?.();
        };
      }, []);

      return intermingles;
    }

    export namespace use {
      export function tryPreserveReferenceStateAction(
        oldIntermingles: List
      ): List {
        const newIntermingles = get();

        // If the old and new intermingle arrays are the same, return the old one:
        if (oldIntermingles === newIntermingles) return oldIntermingles;

        if (oldIntermingles.length !== newIntermingles.length) {
          // If the lengths are different then they are not the same - update:
          return newIntermingles;
        }

        if (
          newIntermingles.every((newIntermingle, index) =>
            equals(newIntermingle, oldIntermingles[index])
          )
        ) {
          // If each intermingle in the arrays are the same, return the old array:
          return oldIntermingles;
        }

        return newIntermingles;
      }
    }

    export function get(): List {
      if (typeof window === 'undefined') return get.defaultValue;
      return getPlacementList().filter(placementHasIntermingle);
    }

    export namespace get {
      export const defaultValue: List = [];
    }
  }

  /**
   * Checks for equality based on the properties used in {@linkcode useSearchPositionInstruction}.
   */
  export function equals(
    a: Nullable<Intermingle>,
    b: Nullable<Intermingle>
  ): boolean {
    if (a === b) return true;
    if (!(a && b)) return false;

    return (
      a.placementId === b.placementId &&
      a.adServer.type === b.adServer.type &&
      a.intermingle?.grid === b.intermingle?.grid &&
      a.intermingle?.list === b.intermingle?.list
    );
  }
}
